import React, { forwardRef } from 'react';
import { Avatar, AvatarGroup, Tooltip, Typography, Fade } from '@mui/material';
import { NumericFormat } from 'react-number-format';

let currentColorIndex = 0;
let currentTestColorIndex = 0;

const getDaySuffix = day => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  const lastDigit = day % 10;
  switch (lastDigit) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatCreatedAtColumn = params => {
  const createdAtDate = new Date(params.row.createdAt);

  // Format the time
  const timeOptions = { hour: '2-digit', minute: '2-digit' };
  const formattedTime = createdAtDate.toLocaleTimeString('en-US', timeOptions);

  // Format the date components
  const day = createdAtDate.getDate();
  const month = createdAtDate.toLocaleString('en-US', { month: 'short' });
  const year = createdAtDate.getFullYear();

  // Get the day suffix
  const daySuffix = getDaySuffix(day);

  return (
    <div>
      <div>{formattedTime}</div>
      <div>{`${month} ${day}${daySuffix}, ${year}`}</div>
    </div>
  );
};

export const formatStatusColumn = params => {
  if (params.row.status) {
    let color;
    switch (params.row.status) {
      case 'Awaiting Approval':
      case 'Awaiting Signing':
      case 'Draft':
        color = '#EFAC00';
        break;
      case 'Credited':
      case 'Approved':
      case 'Signed':
      case 'Delivered':
        color = '#B7D423';
        break;
      case 'Rejected':
      case 'Customer Rejected':
      case 'Unsuccessful':
        color = '#E96666';
        break;
      default:
        color = '';
    }

    return <div style={{ color }}>{params.row.status}</div>;
  }
  return null;
};

export const formatAmountColumn = value => {
  return value && value !== '-' ? `₹${(+value).toLocaleString('en-IN')}` : '-';
};

export const formatDateAndTime = createdAtString => {
  if (typeof createdAtString === 'undefined' || createdAtString === null || createdAtString === '-') {
    return null;
  }
  const [timePart = null, dateString = null, yearPart = null] = createdAtString.split(',').map(str => str.trim());

  return (
    <>
      {timePart}
      <br />
      {dateString}, {yearPart}
    </>
  );
};

export const formatDateInNumeric = (dateString, yearPart) => {
  const months = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12'
  };

  const [, monthName, day] = dateString.match(/(\w+)\s+(\d+)(?:st|nd|rd|th)/) || [];
  const month = months[monthName];
  const formattedDate = `${day} / ${month} / ${yearPart}`;

  return formattedDate;
};

export const getNextColor = () => {
  const colors = ['#fbfdf0', '#fff9d8', '#fcefed'];
  currentColorIndex = (currentColorIndex + 1) % colors.length;
  return colors[currentColorIndex];
};

export const getTextNextColor = () => {
  const colors = ['#D3E966', '#FFDA15', '#E9BC66'];
  currentTestColorIndex = (currentTestColorIndex + 1) % colors.length;
  return colors[currentTestColorIndex];
};

export const formatAvatarGroup = recipientData => {
  if (!recipientData || recipientData.length === 0) {
    return '-';
  }

  const maxCount = 2;

  const getNextColor = index => {
    const colors = ['#fbfdf0', '#fff9d8', '#fcefed'];
    return colors[index % colors.length];
  };

  const getTextNextColor = index => {
    const colors = ['#D3E966', '#FFDA15', '#E9BC66'];
    return colors[index % colors.length];
  };

  const renderRecipientAvatars = () => {
    return recipientData.slice(0, maxCount).map((recipient, index) => (
      <Tooltip
        key={recipient.recipientId}
        title={
          <Typography>
            {[recipient?.recipientFirstName, recipient?.recipientMiddleName, recipient?.recipientLastName]
              .filter(Boolean)
              .join(' ')}
          </Typography>
        }
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 600 }}
        arrow
      >
        <Avatar
          style={{
            backgroundColor: getNextColor(index),
            color: getTextNextColor(index),
            border: '1px solid',
            height: '35px',
            width: '35px',
            fontSize: '18px'
          }}
        >
          {[recipient?.recipientFirstName?.[0], recipient?.recipientMiddleName?.[0], recipient?.recipientLastName?.[0]]
            .filter(Boolean)
            .join('')}
        </Avatar>
      </Tooltip>
    ));
  };

  const renderOverflowAvatar = () => {
    const overflowCount = recipientData.length - maxCount;
    return (
      <Tooltip
        title={
          <Typography>
            {recipientData.slice(maxCount).map(recipient => (
              <div key={recipient.recipientId}>
                {[recipient?.recipientFirstName, recipient?.recipientMiddleName, recipient?.recipientLastName]
                  .filter(Boolean)
                  .join(' ')}
              </div>
            ))}
          </Typography>
        }
        arrow
        enterDelay={500}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 600 }}
      >
        <Avatar style={{ height: '35px', width: '35px', fontSize: '18px' }}>+{overflowCount}</Avatar>
      </Tooltip>
    );
  };

  return (
    <div style={{ display: 'flex' }}>
      <AvatarGroup>
        {renderRecipientAvatars()}
        {recipientData.length > maxCount && renderOverflowAvatar()}
      </AvatarGroup>
    </div>
  );
};

export const getOrdinalSuffix = number => {
  if (number >= 11 && number <= 13) {
    return `${number}th`;
  }

  const lastDigit = number % 10;
  const suffix = lastDigit === 1 ? 'st' : lastDigit === 2 ? 'nd' : lastDigit === 3 ? 'rd' : 'th';

  return `${number}${suffix}`;
};

export const formatDatesAndTimes = time => {
  if (!time) return '';
  const inputDate = new Date(time);

  if (isNaN(inputDate.getTime())) return 'Invalid Date';

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const day = inputDate.getDate();
  const month = months[inputDate.getMonth()];
  const year = inputDate.getFullYear();
  const hours = inputDate.getHours();
  const minutes = inputDate.getMinutes();

  const formattedDate = `${month} ${getOrdinalSuffix(day)}, ${year}`;
  const formattedTime = `${hours % 12 || 12}:${minutes < 10 ? '0' : ''}${minutes} ${hours >= 12 ? 'PM' : 'AM'}`;

  return (
    <>
      {formattedTime}
      <br />
      {formattedDate}
    </>
  );
};

export const resetState = setStateFunctions => {
  const { setSelectedDropdownValue, setStartDate, setEndDate, setSelectedQuickDate, setPageCount, setPageSize } =
    setStateFunctions;

  setSelectedDropdownValue(90);
  setStartDate(null);
  setEndDate(null);
  setSelectedQuickDate({ quickDateFrom: null, quickDateTo: null });
  setPageCount(1);
  setPageSize(10);
};

export const formatDateVal = date => {
  const event = new Date(date);
  let formatedDate = event.toLocaleString('en-US', { timeZone: 'UTC' });
  return formatedDate?.split(',')[0];
};
export const formatTimeVal = date => {
  const event = new Date(date);
  let formatedDate = event.toLocaleString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' });
  return formatedDate;
};

export const formatDate = dateString => {
  const date = new Date(dateString);

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

export const NumericFormatCustom = forwardRef(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={values => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        });
      }}
      thousandsGroupStyle='lakh'
      thousandSeparator=','
    />
  );
});
